/**
 * pp-header-search — search bar with typewriter placeholder + predictive
 * dropdown + category filter.
 *
 * Extracted del inline `<script>` en snippets/pp-header-search.liquid
 * (~450 líneas) 2026-05-20 para cacheability cross-page. El IIFE arranca
 * en DOMContentLoaded o inmediato si el documento ya cargó, y se
 * re-inicializa en shopify:section:load (theme editor).
 *
 * No Liquid interpolation aquí — el snippet padre pasa todos los datos
 * via data-* attributes en el wrapper.
 */
(function () {
  function initSearch() {
      document.querySelectorAll('[data-pp-search-wrap]').forEach(function (wrap) {
        if (wrap.dataset.ppInit === '1') return;
        wrap.dataset.ppInit = '1';

        const form = wrap.querySelector('[data-pp-search]');
        const input = wrap.querySelector('[data-pp-search-input]');
        const catSelect = wrap.querySelector('[data-pp-search-cat]');
        const dropdown = wrap.querySelector('[data-pp-search-dropdown]');

        // ── Typewriter placeholder ─────────────────────────────
        if (input) {
          let phrases = [];
          try {
            phrases = JSON.parse(input.getAttribute('data-pp-phrases') || '[]');
          } catch (e) {}

          if (phrases.length > 0) {
            const prefix = 'Estoy buscando ';
            let phraseIdx = 0;
            let charIdx = 0;
            let deleting = false;
            let timer;

            function tick() {
              clearTimeout(timer);
              // Pausar si user enfocó y escribió algo
              if (input.value && input.value.length > 0) {
                timer = setTimeout(tick, 800);
                return;
              }

              const phrase = phrases[phraseIdx];
              if (!deleting) {
                charIdx++;
                input.setAttribute('placeholder', prefix + phrase.slice(0, charIdx));
                if (charIdx >= phrase.length) {
                  deleting = true;
                  timer = setTimeout(tick, 1600); // pausa antes de borrar
                  return;
                }
                timer = setTimeout(tick, 60 + Math.random() * 50);
              } else {
                charIdx--;
                input.setAttribute('placeholder', prefix + phrase.slice(0, charIdx));
                if (charIdx <= 0) {
                  deleting = false;
                  phraseIdx = (phraseIdx + 1) % phrases.length;
                  timer = setTimeout(tick, 220);
                  return;
                }
                timer = setTimeout(tick, 30);
              }
            }
            tick();
          }
        }

        // ── Dropdown show/hide + backdrop ─────────────────────
        // Movemos el backdrop al body para que su position:fixed sea
        // relativo al viewport (no al header sticky, que crea containing
        // block y limita el alcance del fixed).
        const backdrop = document.querySelector('[data-pp-search-backdrop]');
        if (backdrop && backdrop.parentElement !== document.body) {
          document.body.appendChild(backdrop);
        }
        if (dropdown && input) {
          let hideTimer;
          function show() {
            clearTimeout(hideTimer);
            dropdown.removeAttribute('hidden');
            input.setAttribute('aria-expanded', 'true');
            wrap.classList.add('pp-search--open');
            document.body.classList.add('pp-search-open');
            if (backdrop) backdrop.removeAttribute('hidden');
          }
          function hide() {
            dropdown.setAttribute('hidden', '');
            input.setAttribute('aria-expanded', 'false');
            wrap.classList.remove('pp-search--open');
            document.body.classList.remove('pp-search-open');
            if (backdrop) backdrop.setAttribute('hidden', '');
          }
          input.addEventListener('focus', show);

          /*
           * CERRAR SÓLO CUANDO EL FOCO SALE DE VERDAD.
           *
           * Hasta el 5-sep-2026 esto escuchaba `blur` del input y cerraba a
           * los 220 ms pase lo que pase; lo único que cancelaba el cierre era
           * un `mousedown` sobre el dropdown. O sea: los resultados eran
           * alcanzables con el RATÓN y con nada más. Quien tecleara "caja
           * 30x30" y quisiera bajar con el teclado veía desaparecer el panel
           * antes de llegar.
           *
           * `focusout` sobre el CONTENEDOR —no sobre el input— trae
           * `relatedTarget`: a dónde se va el foco. Si va a algo de adentro
           * (un resultado), no se cierra. El temporizador se queda para el
           * caso del ratón, donde `relatedTarget` es null al soltar sobre el
           * fondo, y el `mousedown` de abajo lo sigue cancelando.
           */
          wrap.addEventListener('focusout', function (e) {
            if (e.relatedTarget && wrap.contains(e.relatedTarget)) return;
            hideTimer = setTimeout(hide, 220);
          });
          wrap.addEventListener('focusin', function () {
            clearTimeout(hideTimer);
          });
          dropdown.addEventListener('mousedown', function () {
            clearTimeout(hideTimer);
          });
          document.addEventListener('click', function (e) {
            if (!wrap.contains(e.target)) hide();
          });
          if (backdrop) {
            backdrop.addEventListener('mousedown', function () {
              hide();
              input.blur();
            });
          }
          document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') {
              hide();
              input.blur();
            }
          });
        }

        // ── Live predictive search (section-rendered HTML) ────
        const liveBox = wrap.querySelector('[data-pp-search-live]');
        const defaultBox = wrap.querySelector('[data-pp-search-default]');
        const skelBox = wrap.querySelector('[data-pp-search-skel]');

        function showDefault() {
          if (defaultBox) defaultBox.removeAttribute('hidden');
          if (liveBox) liveBox.setAttribute('hidden', '');
          if (skelBox) skelBox.setAttribute('hidden', '');
        }
        function showLive() {
          if (defaultBox) defaultBox.setAttribute('hidden', '');
          if (skelBox) skelBox.setAttribute('hidden', '');
          if (liveBox) liveBox.removeAttribute('hidden');
        }
        // Skeleton mientras el fetch está en curso (feedback inmediato al teclear).
        function showSkel() {
          if (defaultBox) defaultBox.setAttribute('hidden', '');
          if (liveBox) liveBox.setAttribute('hidden', '');
          if (skelBox) skelBox.removeAttribute('hidden');
        }

        // Normaliza la query del usuario para que matchee títulos
        // independiente del formato:
        //  · "15x15x15" / "15 X 15 X 15" / "15×15×15" → "15 x 15 x 15"
        //  · "15cm x 15cm" / "15 cm x 15 cm" → "15 x 15"
        //  · "p1111" / "p 1111" / "P-1111" → "P-1111" (SKU canonical)
        //
        // SKU: Shopify tokeniza por separadores, así que "P-1111" se indexa
        // como ["P", "1111"] y un query "P1111" sin separador no matchea.
        // Reescribimos a "P-1111" para que sí matchee. "1111" solo
        // (puros dígitos) se manda tal cual — Shopify lo encuentra vía el
        // token numérico.
        function normalizeQuery(raw) {
          let q = (raw || '').trim();
          if (!q) return '';

          // SKU-like: 1-5 letras + opcional separador + 3+ dígitos + opcional letra
          const skuMatch = q.match(/^([A-Za-z]{1,5})[\s\-_]?(\d{3,})([A-Za-z]?)$/);
          if (skuMatch) {
            return skuMatch[1].toUpperCase() + '-' + skuMatch[2] + skuMatch[3].toUpperCase();
          }

          // SKU completo con guion ("CPK-190-48X50", "CA-184-7110A",
          // "ITT-6A042D-REDBT"): va TAL CUAL. Shopify lo encuentra entero por
          // variants.sku; la regla de medidas de abajo lo partía (48X50 →
          // "48 x 50") y ya no aparecía — 116 de 947 SKUs, las cintas
          // personalizables (14-oct-2026). Una medida empieza con número,
          // así que "15x15x15" sigue por su camino.
          if (/^[A-Za-z]{1,6}-[A-Za-z0-9-]+$/.test(q)) {
            return q.toUpperCase();
          }

          // Quitar sufijos de unidad pegados a números (15cm, 10 mm, 2 in, etc.)
          q = q.replace(/(\d)\s*(cm|mm|in|inch|pulgadas?|pulg)\b\.?/gi, '$1');
          // Normalizar separadores de medida (×, *, X) a " x " — solo entre dígitos
          // para no romper palabras con x (e.g. "examen", "México")
          let prev;
          do {
            prev = q;
            q = q.replace(/(\d)\s*[xX×*]\s*(\d)/g, '$1 x $2');
          } while (q !== prev);
          // Colapsar espacios múltiples
          q = q.replace(/\s+/g, ' ').trim();
          return q;
        }

        // Si q es "N x N" o "N x N x N", devuelve [N, N, N]; si no, null.
        function extractDims(q) {
          const m = q.match(/^(\d+)\s*x\s*(\d+)(?:\s*x\s*(\d+))?$/i);
          if (!m) return null;
          const dims = [parseInt(m[1], 10), parseInt(m[2], 10)];
          if (m[3]) dims.push(parseInt(m[3], 10));
          return dims;
        }

        // Extrae las dimensiones del título de un producto. Soporta "N x N",
        // "N x N x N" y decimales (e.g. "41.8 x 27.9 x 24.9 cm"). Devuelve
        // array o null si no hay match.
        function extractDimsFromTitle(title) {
          const m = (title || '').match(/(\d+(?:\.\d+)?)\s*[xX×]\s*(\d+(?:\.\d+)?)(?:\s*[xX×]\s*(\d+(?:\.\d+)?))?/);
          if (!m) return null;
          const dims = [parseFloat(m[1]), parseFloat(m[2])];
          if (m[3]) dims.push(parseFloat(m[3]));
          return dims;
        }

        // Decide si las dimensiones del producto matchean las del query.
        // Misma dimensionalidad (2D vs 3D) y cada dim dentro de tolerancia.
        function dimsMatch(productDims, queryDims, tolerance) {
          if (!productDims || productDims.length !== queryDims.length) return false;
          // Comparar ordenados — la caja puede venir con dims en cualquier orden
          const a = queryDims.slice().sort(function (x, y) { return x - y; });
          const b = productDims.slice().sort(function (x, y) { return x - y; });
          for (let i = 0; i < a.length; i++) {
            if (Math.abs(a[i] - b[i]) > tolerance) return false;
          }
          return true;
        }

        // Filtra los productos del HTML de la section que NO matcheen
        // dimensionalmente. Páginas/artículos se preservan. Devuelve la
        // cantidad de productos que quedaron.
        function filterDimensionalMatches(content, queryDims, tolerance) {
          if (!content || !queryDims) return null;
          const sections = content.querySelectorAll('.pp-search__dropdown-section');
          let productCount = 0;
          sections.forEach(function (section) {
            const heading = section.querySelector('.pp-search__dropdown-heading');
            const headingText = heading ? heading.textContent.trim().toLowerCase() : '';
            if (headingText !== 'productos') return;
            const items = section.querySelectorAll('.pp-search__live-item');
            items.forEach(function (item) {
              const titleEl = item.querySelector('.pp-search__live-title');
              const dims = extractDimsFromTitle(titleEl ? titleEl.textContent : '');
              if (dimsMatch(dims, queryDims, tolerance)) {
                productCount++;
              } else {
                item.remove();
              }
            });
            if (productCount === 0) {
              // Sin productos: quitar la sección entera (incluyendo "Ver todos")
              section.remove();
            }
          });
          return productCount;
        }

        // Genera candidatos de medidas cercanas para fallback:
        // 1. Redondeadas al múltiplo de 5 más cercano (14→15, 31→30, 27→25)
        // 2. Sin la última dimensión (3D → 2D, ej. caja → bolsa/sobre)
        function generateDimCandidates(dims) {
          const cs = [];
          const rounded = dims.map(function (d) {
            return Math.max(5, Math.round(d / 5) * 5);
          });
          cs.push(rounded.join(' x '));
          if (dims.length > 1) {
            cs.push(rounded.slice(0, -1).join(' x '));
          }
          // Dedup
          return cs.filter(function (c, i, arr) { return arr.indexOf(c) === i; });
        }

        let liveTimer;
        let liveGen = 0;

        // Una sola request a la section endpoint. Devuelve { content, total }.
        function fetchOnce(q, signal) {
          // resources[options][fields] le dice a Shopify qué campos buscar.
          // Por default no incluye variants.sku — al agregarlo aquí "P-1111"
          // matchea productos por SKU. tag/vendor/product_type ensanchan los
          // matches sin romper relevancia.
          const url = '/search/suggest?q=' + encodeURIComponent(q)
            + '&section_id=pp-predictive-search'
            + '&resources[type]=product,page,article&resources[limit]=8'
            + '&resources[options][unavailable_products]=last'
            + '&resources[options][fields]=title,product_type,variants.title,variants.sku,tag,vendor';
          return fetch(url, { signal: signal })
            .then(function (r) { return r.text(); })
            .then(function (html) {
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const content = doc.querySelector('[data-pp-live-content]');
              const total = content
                ? parseInt(content.getAttribute('data-total') || '0', 10)
                : 0;
              return { content: content, total: total, query: q };
            });
        }

        // Aplica el filtro dimensional si aplica. Devuelve { effectiveTotal, content }
        // donde content es un clon filtrado. Si no es dimensional, devuelve los
        // datos originales.
        function applyDimFilter(result, queryDims) {
          if (!queryDims || !result.content) {
            return { effectiveTotal: result.total, content: result.content };
          }
          const clone = result.content.cloneNode(true);
          const productCount = filterDimensionalMatches(clone, queryDims, 5);
          // Si quedan productos, contar páginas/artículos también
          let total = productCount || 0;
          const miniItems = clone.querySelectorAll('.pp-search__live-mini-item');
          total += miniItems.length;
          return { effectiveTotal: total, content: clone, productCount: productCount };
        }

        function fetchLive(q) {
          const myGen = ++liveGen;
          const controller = new AbortController();
          const queryDims = extractDims(q);

          fetchOnce(q, controller.signal).then(function (originalResult) {
            if (myGen !== liveGen) return;

            const filtered = applyDimFilter(originalResult, queryDims);

            // Si hay productos relevantes (o no es dimensional y hay resultados), render directo
            if (filtered.effectiveTotal > 0 && (!queryDims || filtered.productCount > 0)) {
              renderResult({ content: filtered.content, total: filtered.effectiveTotal }, null);
              return;
            }

            // No es dimensional, simplemente 0 resultados: render empty state
            if (!queryDims) {
              renderResult(originalResult, null);
              return;
            }

            // Query dimensional + 0 productos relevantes: probar candidatos cercanos
            const candidates = generateDimCandidates(queryDims).filter(function (c) {
              return c !== q;
            });

            function tryNext(i) {
              if (myGen !== liveGen) return;
              if (i >= candidates.length) {
                // Sin suerte — mostramos el empty state original
                renderResult(originalResult, null);
                return;
              }
              fetchOnce(candidates[i], controller.signal).then(function (r) {
                if (myGen !== liveGen) return;
                const candDims = extractDims(candidates[i]);
                const cFiltered = applyDimFilter(r, candDims);
                if (cFiltered.productCount > 0) {
                  renderResult(
                    { content: cFiltered.content, total: cFiltered.effectiveTotal },
                    { from: q, to: candidates[i] }
                  );
                } else {
                  tryNext(i + 1);
                }
              }).catch(function () { tryNext(i + 1); });
            }
            tryNext(0);
          }).catch(function () {});
        }

        // Inyecta pills de fallback (del menú configurado en el snippet) dentro
        // del empty state si la section no las trajo y existe el template.
        function injectFallbackPills() {
          const emptyInline = liveBox.querySelector('.pp-search__empty-inline');
          if (!emptyInline) return;
          if (emptyInline.querySelector('.pp-search__pills')) return;
          const template = wrap.querySelector('[data-pp-search-fallback-pills]');
          if (!template) return;
          const pills = template.querySelectorAll('.pp-search__pill');
          if (pills.length === 0) return;
          const allLink = emptyInline.querySelector('.pp-search__pill--all');
          const secondary = document.createElement('p');
          secondary.className = 'pp-search__empty-secondary';
          secondary.textContent = 'Quizá te interesa explorar por categoría:';
          const pillsDiv = document.createElement('div');
          pillsDiv.className = 'pp-search__pills';
          pills.forEach(function (p) { pillsDiv.appendChild(p.cloneNode(true)); });
          if (allLink) {
            emptyInline.insertBefore(secondary, allLink);
            emptyInline.insertBefore(pillsDiv, allLink);
          } else {
            emptyInline.appendChild(secondary);
            emptyInline.appendChild(pillsDiv);
          }
        }

        function renderResult(result, suggested) {
          if (!liveBox) return;
          while (liveBox.firstChild) liveBox.removeChild(liveBox.firstChild);

          if (suggested) {
            const banner = document.createElement('div');
            banner.className = 'pp-search__suggested-banner';
            const t1 = document.createElement('span');
            t1.textContent = 'No encontramos "' + suggested.from + '". Te mostramos lo más parecido: ';
            const t2 = document.createElement('strong');
            t2.textContent = suggested.to;
            banner.appendChild(t1);
            banner.appendChild(t2);
            liveBox.appendChild(banner);
          }

          if (result.content) {
            liveBox.appendChild(document.importNode(result.content, true));
          }
          // Si el render terminó en empty state, agregamos las pills
          injectFallbackPills();
          showLive();
        }

        if (input) {
          input.addEventListener('input', function () {
            const raw = (input.value || '').trim();
            clearTimeout(liveTimer);
            if (raw.length < 2) { showDefault(); return; }
            showSkel();
            const q = normalizeQuery(raw);
            liveTimer = setTimeout(function () { fetchLive(q); }, 180);
          });
        }

        // ── Carousel auto-rotate (cada 5s) ────────────────────
        const track = wrap.querySelector('[data-pp-carousel-track]');
        const prev = wrap.querySelector('[data-pp-carousel-prev]');
        const next = wrap.querySelector('[data-pp-carousel-next]');
        if (track) {
          function scrollByCards(dir) {
            const card = track.querySelector('.pp-search__suggestion');
            if (!card) return;
            const cardWidth = card.offsetWidth + 12;
            track.scrollBy({ left: dir * cardWidth * 3, behavior: 'smooth' });
          }
          if (next) next.addEventListener('click', function () { scrollByCards(1); });
          if (prev) prev.addEventListener('click', function () { scrollByCards(-1); });

          // Auto-rotate cada 5s (solo cuando dropdown está abierto)
          let autoTimer;
          function startAuto() {
            stopAuto();
            autoTimer = setInterval(function () {
              const max = track.scrollWidth - track.clientWidth;
              if (track.scrollLeft >= max - 4) {
                track.scrollTo({ left: 0, behavior: 'smooth' });
              } else {
                scrollByCards(1);
              }
            }, 5000);
          }
          function stopAuto() { if (autoTimer) clearInterval(autoTimer); }
          if (input) {
            input.addEventListener('focus', startAuto);
            input.addEventListener('blur', stopAuto);
          }
          track.addEventListener('mouseenter', stopAuto);
          track.addEventListener('mouseleave', function () {
            if (wrap.classList.contains('pp-search--open')) startAuto();
          });
        }

        // ── Submit con categoría + query normalizada ──────────
        // En páginas de colección hay un wrap del buscador SIN <form>
        // ([data-pp-search] ausente) y este listener tronaba con
        // "Cannot read properties of null" en cada carga (hallazgo del
        // frente de CLS 2026-08-27, preexistente desde julio). Sin form
        // no hay submit que interceptar: el resto del wrap (dropdown,
        // sugerencias, carousel) ya quedó cableado arriba.
        if (!form) return;
        form.addEventListener('submit', function (e) {
          const raw = (input && input.value || '').trim();
          const normalized = normalizeQuery(raw);
          const catUrl = catSelect ? catSelect.value : '';
          if (catUrl) {
            e.preventDefault();
            const url = new URL(catUrl, window.location.origin);
            if (normalized) url.searchParams.set('q', normalized);
            window.location.href = url.toString();
            return;
          }
          // Reescribimos el input para que /search?q=... use la query normalizada
          if (input && normalized && normalized !== raw) {
            input.value = normalized;
          }
        });
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initSearch);
    } else {
      initSearch();
    }
    document.addEventListener('shopify:section:load', initSearch);
  })();
